import React from 'react';
import { TrendingUp, TrendingDown, Target, DollarSign, Users, CheckCircle, Activity } from 'lucide-react';

/**
 * MetricCard Component
 * Displays a single metric with current value, target value, and progress
 */
const MetricCard = ({ metric, onEdit, onDelete, editable = false }) => {
  const {
    MetricID,
    MetricType,
    MetricName,
    TargetValue,
    CurrentValue = 0,
    Unit,
    PercentComplete
  } = metric;

  // Calculate percentage if not provided
  const percentComplete = PercentComplete !== undefined 
    ? parseFloat(PercentComplete) 
    : TargetValue > 0 ? (CurrentValue / TargetValue) * 100 : 0;

  // Determine status color and icon
  const getStatusColor = () => {
    if (percentComplete >= 100) return 'text-green-600 bg-green-50';
    if (percentComplete >= 80) return 'text-blue-600 bg-blue-50';
    if (percentComplete >= 50) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getStatusBadge = () => {
    if (percentComplete >= 100) return { label: 'Achieved', color: 'bg-green-100 text-green-800' };
    if (percentComplete >= 80) return { label: 'On Track', color: 'bg-blue-100 text-blue-800' };
    if (percentComplete >= 50) return { label: 'At Risk', color: 'bg-yellow-100 text-yellow-800' };
    return { label: 'Behind', color: 'bg-red-100 text-red-800' };
  };

  const getMetricIcon = () => {
    switch (MetricType) {
      case 'Revenue':
        return <DollarSign className="w-5 h-5" />;
      case 'NewCustomers':
        return <Users className="w-5 h-5" />;
      case 'DealsWon':
        return <CheckCircle className="w-5 h-5" />;
      case 'Activities':
        return <Activity className="w-5 h-5" />;
      default:
        return <Target className="w-5 h-5" />;
    }
  };

  const formatValue = (value) => {
    if (Unit === 'THB' || Unit === 'USD') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: Unit || 'THB',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    }
    return new Intl.NumberFormat('en-US').format(value);
  };

  const statusBadge = getStatusBadge();
  const trend = CurrentValue >= TargetValue;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className={`p-2 rounded-lg ${getStatusColor()}`}>
            {getMetricIcon()}
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-900">
              {MetricName || MetricType}
            </h3>
            <p className="text-xs text-gray-500">{MetricType}</p>
          </div>
        </div>
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.color}`}>
          {statusBadge.label}
        </span>
      </div>

      {/* Values */}
      <div className="space-y-2 mb-4">
        <div className="flex justify-between items-baseline">
          <span className="text-xs text-gray-500">Current</span>
          <span className="text-lg font-semibold text-gray-900">
            {formatValue(CurrentValue)}
          </span>
        </div>
        <div className="flex justify-between items-baseline">
          <span className="text-xs text-gray-500">Target</span>
          <span className="text-sm text-gray-600">
            {formatValue(TargetValue)}
          </span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="relative pt-1">
        <div className="flex mb-2 items-center justify-between">
          <div>
            <span className="text-xs font-semibold inline-block text-gray-700">
              {percentComplete.toFixed(1)}%
            </span>
          </div>
          <div className="text-right">
            <span className="text-xs inline-flex items-center">
              {trend ? (
                <TrendingUp className="w-3 h-3 text-green-500 mr-1" />
              ) : (
                <TrendingDown className="w-3 h-3 text-red-500 mr-1" />
              )}
              <span className={trend ? 'text-green-600' : 'text-red-600'}>
                {Math.abs(TargetValue - CurrentValue) > 0 ? formatValue(Math.abs(TargetValue - CurrentValue)) : '0'}
              </span>
            </span>
          </div>
        </div>
        <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200">
          <div
            style={{ width: `${Math.min(percentComplete, 100)}%` }}
            className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center transition-all duration-500 ${
              percentComplete >= 100 ? 'bg-green-500' :
              percentComplete >= 80 ? 'bg-blue-500' :
              percentComplete >= 50 ? 'bg-yellow-500' :
              'bg-red-500'
            }`}
          />
        </div>
      </div>

      {/* Actions */}
      {editable && (
        <div className="flex justify-end space-x-2 mt-4 pt-4 border-t border-gray-100">
          <button
            onClick={() => onEdit && onEdit(metric)}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
          >
            Edit
          </button>
          <button
            onClick={() => onDelete && onDelete(MetricID)}
            className="text-xs text-red-600 hover:text-red-800 font-medium"
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
};

export default MetricCard;


